import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { getOperacoes, aprovarOperacao, rejeitarOperacao } from '@/mocks/backoffice/operacoesHelpers';

export default function BackofficeFinanceiroPage() {
  const navigate = useNavigate();
  const { session } = useBackofficeAuth();
  const [ops, setOps] = useState(getOperacoes());
  const [filtroStatus, setFiltroStatus] = useState<string>('pendente');
  const [filtroTipo, setFiltroTipo] = useState<string>('todos');
  const [rejeitandoId, setRejeitandoId] = useState<string | null>(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [feedback, setFeedback] = useState('');

  const operacoesFiltradas = useMemo(() => {
    return ops.filter(o => {
      if (filtroStatus !== 'todos' && o.status !== filtroStatus) return false;
      if (filtroTipo !== 'todos' && o.tipo !== filtroTipo) return false;
      return true;
    });
  }, [ops, filtroStatus, filtroTipo]);

  const tipoLabel: Record<string, string> = {
    saque_pix: 'Saque PIX',
    credito_pontos: 'Compra de Pontos',
    debito_pontos: 'Débito',
    ajuste_saldo: 'Ajuste',
    estorno: 'Estorno',
    compra_pontos: 'Compra Pontos',
  };

  const tipoColors: Record<string, { bg: string; text: string }> = {
    saque_pix: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
    credito_pontos: { bg: 'bg-blue-50', text: 'text-blue-600' },
    debito_pontos: { bg: 'bg-red-50', text: 'text-red-600' },
    ajuste_saldo: { bg: 'bg-amber-50', text: 'text-amber-600' },
    estorno: { bg: 'bg-orange-50', text: 'text-orange-600' },
    compra_pontos: { bg: 'bg-blue-50', text: 'text-blue-600' },
  };

  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    pendente: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Pendente' },
    aprovado: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Aprovado' },
    rejeitado: { bg: 'bg-red-50', text: 'text-red-600', label: 'Rejeitado' },
    processando: { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Processando' },
  };

  const handleAprovar = (id: string) => {
    if (aprovarOperacao(id, session.nome || 'Admin')) {
      setOps([...getOperacoes()]);
      setFeedback('Operação aprovada!');
      setTimeout(() => setFeedback(''), 2000);
    }
  };

  const handleRejeitar = (id: string) => {
    if (!motivoRejeicao.trim()) return;
    if (rejeitarOperacao(id, session.nome || 'Admin', motivoRejeicao.trim())) {
      setOps([...getOperacoes()]);
      setRejeitandoId(null);
      setMotivoRejeicao('');
      setFeedback('Operação rejeitada!');
      setTimeout(() => setFeedback(''), 2000);
    }
  };

  // Stats
  const stats = {
    pendentes: ops.filter(o => o.status === 'pendente').length,
    valorPendente: ops.filter(o => o.status === 'pendente').reduce((sum, o) => sum + o.valor, 0),
    aprovadosHoje: ops.filter(o => o.status === 'aprovado' && o.data_aprovacao && new Date(o.data_aprovacao).toDateString() === new Date().toDateString()).length,
    rejeitados: ops.filter(o => o.status === 'rejeitado').length,
  };

  const formatCurrency = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Operações Financeiras</h1>
            <p className="text-sm text-gray-500 mt-1">Aprovação de saques e operações pendentes</p>
          </div>
          {feedback && (
            <span className="text-xs text-emerald-600 font-bold px-3 py-1.5 bg-emerald-50 rounded-lg">
              {feedback}
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                <i className="ri-time-line text-amber-500 text-sm"></i>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-800">{stats.pendentes}</p>
                <p className="text-[10px] text-gray-400">Pendentes</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-emerald-500 text-sm"></i>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-800">{formatCurrency(stats.valorPendente)}</p>
                <p className="text-[10px] text-gray-400">Valor pendente</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <i className="ri-check-double-line text-blue-500 text-sm"></i>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-800">{stats.aprovadosHoje}</p>
                <p className="text-[10px] text-gray-400">Aprovados hoje</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                <i className="ri-close-circle-line text-red-500 text-sm"></i>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-800">{stats.rejeitados}</p>
                <p className="text-[10px] text-gray-400">Rejeitados</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex flex-wrap gap-2">
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
            >
              <option value="todos">Todos os status</option>
              <option value="pendente">Pendentes</option>
              <option value="aprovado">Aprovados</option>
              <option value="rejeitado">Rejeitados</option>
              <option value="processando">Processando</option>
            </select>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
            >
              <option value="todos">Todos os tipos</option>
              <option value="saque_pix">Saque PIX</option>
              <option value="credito_pontos">Compra de Pontos</option>
              <option value="debito_pontos">Débito</option>
              <option value="ajuste_saldo">Ajuste</option>
              <option value="estorno">Estorno</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Operação</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Valor</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Solicitante</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Descrição</th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Data</th>
                  <th className="text-center px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {operacoesFiltradas.map(op => {
                  const tc = tipoColors[op.tipo] || tipoColors.saque_pix;
                  const st = statusConfig[op.status] || statusConfig.pendente;
                  return (
                    <tr key={op.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold text-gray-800">{op.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${tc.bg} ${tc.text}`}>
                          {tipoLabel[op.tipo] || op.tipo}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st.bg} ${st.text}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-bold text-gray-800">{op.valor_formatado}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-xs text-gray-700">{op.usuario_nome}</p>
                          <p className="text-[10px] text-gray-400">{op.usuario_tipo === 'empresa' ? 'Empresa' : 'Participante'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-600 max-w-[200px] truncate">{op.descricao}</p>
                        {op.chave_pix && <p className="text-[10px] text-gray-400 mt-0.5">PIX: {op.chave_pix}</p>}
                        {op.banco && <p className="text-[10px] text-gray-400">Banco: {op.banco}</p>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-[11px] text-gray-500">{new Date(op.data_solicitacao).toLocaleDateString('pt-BR')}</span>
                      </td>
                      <td className="px-4 py-3">
                        {op.status === 'pendente' ? (
                          <div className="flex items-center justify-center space-x-1">
                            <button
                              onClick={() => handleAprovar(op.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-all cursor-pointer"
                              title="Aprovar"
                            >
                              <i className="ri-check-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setRejeitandoId(op.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-all cursor-pointer"
                              title="Rejeitar"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="text-center">
                            {op.aprovado_por && (
                              <p className="text-[10px] text-gray-400">{op.aprovado_por}</p>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {operacoesFiltradas.length === 0 && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-2xl text-gray-300"></i>
              </div>
              <p className="text-sm text-gray-400">Nenhuma operação encontrada</p>
            </div>
          )}
        </div>

        {/* Reject Modal */}
        {rejeitandoId && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setRejeitandoId(null)}></div>
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
                <h3 className="text-sm font-bold text-gray-800 mb-1">Rejeitar Operação</h3>
                <p className="text-xs text-gray-500 mb-4">Informe o motivo da rejeição</p>
                <textarea
                  value={motivoRejeicao}
                  onChange={(e) => setMotivoRejeicao(e.target.value)}
                  placeholder="Motivo da rejeição..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all resize-none mb-4"
                />
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => {
                      setRejeitandoId(null);
                      setMotivoRejeicao('');
                    }}
                    className="px-4 py-2 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleRejeitar(rejeitandoId)}
                    disabled={!motivoRejeicao.trim()}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Rejeitar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </BackofficeLayout>
  );
}